<template>
  <div class="container">
    <Card additional-classes="slds-card_boundary">
      <Input type="checkbox" :label="$t('campaignDesigner.contentSelectionAndTag.onlyInformativeCampaign')" v-model="onlyInformativeCampaign" :disabled="loading" v-if="isCreate || isEdit" />
      <OutputField :label="$t('campaignDesigner.contentSelectionAndTag.onlyInformativeCampaign')" :value="onlyInformativeCampaign" type="checkbox" v-else />
    </Card>

    <Card additional-classes="content slds-m-top--medium">
      <Spinner v-if="loading" />

      <template v-else>
        <!-- Tabs -->
        <TabSet ref="tabSet" tab-set-id="offers_tab_set">
          <Tab :label="$t('campaignDesigner.contentSelectionAndTag.offerSelection')" v-if="!onlyInformativeCampaign" position="0" tab-set-id="offers_tab_set">
            <Card additional-classes="slds-card_boundary">
              <OfferSelector :offers="availableOffers" @offerAdded="addOffer" :editable="isCreate || isEdit" />
            </Card>
            <Card additional-classes="slds-card_boundary" v-if="selectedOffers.length">
              <OffersTable
                :editable="isCreate || isEdit"
                :offers="selectedOffers"
                :tags="tags"
                @removeOffer="removeOffer"
                @tagUpdate="onTagUpdate"
                @update-content-priority="onUpdateContentPriority($event, 'offer')"
                ref="offersTable" />
            </Card>
          </Tab>
          <Tab :label="$t('campaignDesigner.contentSelectionAndTag.contentSetting')" position="1" tab-set-id="offers_tab_set">
            <Card additional-classes="slds-card_boundary">
              <button class="slds-button slds-button_brand" :disabled="isView" @click="addContent">
                {{$t('campaignDesigner.contentSelectionAndTag.insertNewContent')}}
              </button>
            </Card>
            <Card additional-classes="slds-card_boundary" v-if="contents.length">
              <ContentTable
                :contents="contents"
                :editable="isCreate || isEdit"
                :tags="tags"
                @removeContent="removeContent"
                @tagUpdate="onTagUpdateContent"
                @update-content-priority="onUpdateContentPriority($event, 'content')"
                ref="contentTable" />
            </Card>
          </Tab>
        </TabSet>
      </template>
    </Card>
  </div>
</template>
<script>
import _ from 'lodash'
import { getErrorMessage } from '../../utils/utils'
import ContentTable from './offer-manager/ContentTable'
import Card from '../../slds/Card'
import HasMode from '../../utils/HasMode'
import Input from '../../slds/Input'
import OfferSelector from './offer-manager/OfferSelector'
import OffersTable from './offer-manager/OffersTable.vue'
import OutputField from '../../slds/OutputField'
import Spinner from '../../Spinner'
import TabSet from '../../slds/tabs/TabSet'
import Tab from '../../slds/tabs/Tab'

const FLEET_CT = [
  'as_fleet_&_business_newsletter',
  'as_fleet_&_business_commerciali'
]

export default {
  name: 'ContentSelectionAndTag',
  mixins: [HasMode],
  components: {
    Tab,
    TabSet,
    Card,
    ContentTable,
    Input,
    OfferSelector,
    OffersTable,
    OutputField,
    Spinner
  },
  computed: {
    availableOffers () {
      const availableOffers = []
      Object.keys(this.offers).forEach(offer => {
        if (!this.selectedOffers.some(o => o.offerId === offer)) {
          availableOffers.push({ label: `${offer} - ${this.offers[offer][0].title}`, value: _.cloneDeep(offer) })
        }
      })

      return availableOffers
    }
  },
  async activated () {
    try {
      this.$emit('startLoading')
      this.tags = (await this.$store.dispatch('settings/getSettings')).tags
      const campaign = this.callLoadFromStore()
      const brands = campaign.config.campaignData.campaignBrands.selected || campaign.config.campaignData.campaignBrands
      const offersParams = {
        codMarket: campaign.marketCodes,
        excludeFleet: !FLEET_CT.includes(campaign.type),
        masterKey: campaign.masterKey,
        brand: brands.join(','),
        startDate: campaign.startDate,
        endDate: campaign.endDate
      }
      this.offers = _.cloneDeep(await this.$store.dispatch('campaignDesigner/getOffers', offersParams))

      this.$emit('endLoading')
    } catch (error) {
      console.error(error)
      this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
    }
  },
  data () {
    return {
      contents: [],
      offers: [],
      onlyInformativeCampaign: false,
      selectedOffers: [],
      tags: []
    }
  },
  beforeDestroy () {
    this.contents = undefined
    this.offers = undefined
    this.onlyInformativeCampaign = undefined
    this.selectedOffers = undefined
    this.tags = undefined
  },
  methods: {
    onUpdateContentPriority (event, tab) {
      if (tab === 'content') {
        const { index, value } = event
        const content = this.contents[index]
        content.contentPriority = value
      }
      if (tab === 'offer') {
        const { index, value } = event
        const offer = this.selectedOffers[index]
        offer.contentPriority = value
      }
    },
    restoreFullOffers (fullOffers, lightOffers) {
      const offers = []

      let mapped
      if (lightOffers[0] && lightOffers[0].sectionId) {
        mapped = lightOffers
      } else {
        mapped = lightOffers.reduce((acc, val) => {
          acc.push(Object.values(val).flat())
          return acc.flat()
        }, [])
      }

      mapped.forEach(lightOffer => {
        const fullOffer = fullOffers.find(fullOffer => fullOffer.sectionId === lightOffer.sectionId)

        if (fullOffer) {
          offers.push({...fullOffer, tags: lightOffer.tags, contentPriority: lightOffer.contentPriority })
        }
      })

      return offers
    },
    addOffer (event) {
      this.selectedOffers.push(..._.cloneDeep(this.offers[event.offer]))
    },
    addContent () {
      this.contents.push({ description: '', contentPriority: '', tags: [] })
    },
    callLoadFromStore () {
      const currentCampaign = _.cloneDeep(this.$store.getters['campaignDesigner/state'].currentCampaign)
      this.contents = currentCampaign.config.contents
      // if (this.offers) {
      //   this.selectedOffers = this.restoreFullOffers(this.offers, currentCampaign.config.offers)
      // }

      this.onlyInformativeCampaign = !!currentCampaign.config.userPreferences.onlyInformativeCampaign

      return currentCampaign
    },
    getSectionToSave () {
      return {
        config: {
          contents: this.contents,
          offers: this.restoreFullOffers(Object.values(this.offers).flat(), this.selectedOffers)
        },
        userPreferences: {
          onlyInformativeCampaign: this.onlyInformativeCampaign
        }
      }
    },
    onTagUpdate (event) {
      const { index, position, value } = event
      const offer = this.selectedOffers[index]
      offer.tags[position] = value
    },
    onTagUpdateContent (event) {
      const { index, position, value } = event
      const content = this.contents[index]
      content.tags[position] = value
    },
    removeContent (event) {
      this.contents.splice(event.index, 1)
    },
    removeOffer (event) {
      this.selectedOffers.splice(event.index, 1)
    },
    validate () {
      let isContentSectionValid = true
      let isOffersSectionValid = true
      const tabSetRef = this.$refs.tabSet
      tabSetRef.clearTabErrors()

      const contentsTab = tabSetRef.tabs.find(tab => tab.position === 1).id
      let offersTab

      if (this.isCreate || this.isEdit) {
        if (this.contents.length) {
          isContentSectionValid &= !!(this.$refs.contentTable.validate())
          tabSetRef.setErrorTab(contentsTab, !isContentSectionValid)
        } else if (this.onlyInformativeCampaign) {
          tabSetRef.setErrorTab(contentsTab, true)
          this.showToast('error', this.$t('campaignDesigner.contentSelectionAndTag.validate.insertContent'), 'text')
          isContentSectionValid = false
        }

        if (!this.onlyInformativeCampaign) {
          offersTab = tabSetRef.tabs.find(tab => tab.position === 0).id

          if (this.selectedOffers.length) {
            isOffersSectionValid &= !!(this.$refs.offersTable.validate())
            tabSetRef.setErrorTab(offersTab, !isOffersSectionValid)
          } else {
            tabSetRef.setErrorTab(offersTab, true)
            this.showToast('error', this.$t('campaignDesigner.contentSelectionAndTag.validate.insertOffer'), 'text')
            isOffersSectionValid = false
          }
        }

        if (isContentSectionValid && isOffersSectionValid) {
          return true
        } else {
          return undefined
        }
      } else {
        if (this.onlyInformativeCampaign) {
          if (this.contents.length) {
            return true
          } else {
            this.showToast('error', this.$t('campaignDesigner.contentSelectionAndTag.validate.insertContent'), 'text')
            return undefined
          }
        } else {
          if (this.selectedOffers.length) {
            return true
          } else {
            this.showToast('error', this.$t('campaignDesigner.contentSelectionAndTag.validate.insertOffer'), 'text')
            return undefined
          }
        }
      }
    }
  },
  props: {
    config: Object,
    mode: String,
    loading: Boolean
  },
  watch: {
    offers: {
      handler (offers) {
        this.selectedOffers = this.restoreFullOffers(Object.values(offers).flat(), this.$store.getters['campaignDesigner/state'].currentCampaign.config.offers)
      },
      deep: true
    },
    onlyInformativeCampaign (newValue) {
      if (this.$refs.tabSet && newValue) {
        this.selectedOffers = []
        this.$refs.tabSet.setActiveTab(1)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  .container {
    display: flex;
    flex-direction: column;
  }

  .content {
    flex-grow: 1;
  }
</style>
